<script setup lang="ts">
/**
 * Round 42px icon button used in playback / host control toolbars.
 * Pass `active` to render the lit "this mode is engaged" state:
 *  - high-contrast text-primary border
 *  - primary-tinted background
 *  - soft outer halo + warm primary glow
 *  - pulsing pip in the top-right corner
 *
 * Non-prop attrs (aria-label, title, aria-pressed, aria-expanded …) fall
 * through to the underlying <button>, so callers should set them directly
 * on the component.
 */
withDefaults(defineProps<{
  active?: boolean;
  disabled?: boolean;
}>(), {
  active: false,
  disabled: false,
});

defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();
</script>

<template>
  <button
    type="button"
    class="toolbar-icon-toggle"
    :class="{ 'is-active': active }"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <slot />
    <span v-if="$slots.badge" class="toolbar-icon-toggle__badge" aria-hidden="true">
      <slot name="badge" />
    </span>
    <span v-if="active" class="toolbar-icon-toggle__pip" aria-hidden="true" />
  </button>
</template>

<style scoped>
.toolbar-icon-toggle {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid var(--color-surface-hover);
  background: var(--color-surface);
  color: var(--color-text-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.25s ease,
    transform 0.1s ease;
}

.toolbar-icon-toggle:not(:disabled):hover {
  background: color-mix(in srgb, var(--color-surface-hover) 65%, var(--color-surface));
}

.toolbar-icon-toggle:not(:disabled):active {
  transform: scale(0.97);
}

.toolbar-icon-toggle:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ─── Active "lit" state ─────────────────────────────────────────
   Three reinforcing layers so the button reads as engaged at a glance:
   1. Border swaps to text-primary — high contrast against the toolbar.
   2. Background takes a stronger primary tint so the disc itself looks lit.
   3. Box-shadow stack: a soft outer halo (text-primary) doubles the border
      perceptually, followed by a warm primary-colored glow that fades out
      and gives the disc a subtle aura. */
.toolbar-icon-toggle.is-active {
  border-color: var(--color-text-primary);
  background: color-mix(in srgb, var(--color-primary) 32%, var(--color-surface));
}

.toolbar-icon-toggle.is-active:not(:disabled):hover {
  background: color-mix(in srgb, var(--color-primary) 40%, var(--color-surface));
}

/* ─── Active pip ──────────────────────────────────────────────────
   Pulsing dot in the top-right corner. With the lit border + glow doing the
   primary work, the pip is now a confirming detail rather than the only cue.
   Slightly larger and using a tighter ripple than before. */
.toolbar-icon-toggle__pip {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 9px;
  height: 9px;
  border-radius: 100%;
  background: var(--color-text-primary);
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-text-primary) 65%, transparent);
  animation: toolbar-icon-toggle-pulse 1.6s ease-out infinite;
}

@keyframes toolbar-icon-toggle-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-text-primary) 65%, transparent);
  }
  70% {
    box-shadow: 0 0 0 6px color-mix(in srgb, var(--color-text-primary) 0%, transparent);
  }
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-text-primary) 0%, transparent);
  }
}

/* ─── Bottom-right badge slot ─────────────────────────────────────
   Subscript-style annotation anchored to the bottom-right of the disc,
   mirroring the active pip's top-right anchor. The slot is just a
   positioning wrapper — callers supply their own visual treatment
   (text pill, small iconify icon, …) inside the slot. */
.toolbar-icon-toggle__badge {
  position: absolute;
  right: -3px;
  bottom: -3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  pointer-events: none;
}
</style>
